import { useLocale } from '../i18n/LocaleProvider'
import { useEffect, useMemo, useState } from 'react'
import {
  ArrowRight,
  ChartLineUp,
  Check,
  Desktop,
  FileText,
  GlobeHemisphereEast,
  Handshake,
  MagnifyingGlass,
  Package as PackageIcon,
  ShareNetwork,
  type Icon,
} from '@phosphor-icons/react'
import Footer from '../components/Footer'
import Navbar from '../components/Navbar'
import { useInquiry } from '../components/InquirySheet'
import { SiteButton } from '../components/SiteButton'
import { usePageTitle } from '../hooks/usePageTitle'
import './ConfiguratorPage.css'

type PackageId = 'starter' | 'localization' | 'custom'
type ServiceId =
  | 'portfolio'
  | 'research'
  | 'landing-page'
  | 'product-localization'
  | 'business-documents'
  | 'social-media'

type ServiceOption = {
  id: ServiceId
  name: string
  description: string
  price: number
  icon: Icon
}

type PackageOption = {
  id: PackageId
  name: string
  shortName: string
  audience: string
  description: string
  price: number
  includedServiceIds: ServiceId[]
  highlights: string[]
  badge?: string
}

const SERVICE_OPTIONS: ServiceOption[] = [
  {
    id: 'portfolio',
    name: 'Portfolio Creation',
    description: 'A Japan-ready company and offering portfolio.',
    price: 4_000,
    icon: PackageIcon,
  },
  {
    id: 'research',
    name: 'Market Research & Analysis',
    description: 'Market, competitor, and opportunity analysis for Japan.',
    price: 4_000,
    icon: MagnifyingGlass,
  },
  {
    id: 'landing-page',
    name: 'Japan-Localized Landing Page',
    description: 'A localized web experience built for Japanese customers.',
    price: 5_000,
    icon: GlobeHemisphereEast,
  },
  {
    id: 'product-localization',
    name: 'Product Localization',
    description: 'UI/UX strings, in-app copy, store listings, and help content up to 5,000 strings.',
    price: 6_000,
    icon: Desktop,
  },
  {
    id: 'business-documents',
    name: 'Business Document Translation',
    description: 'Japanese translation for client-facing business materials.',
    price: 1_500,
    icon: FileText,
  },
  {
    id: 'social-media',
    name: 'Social Media Setup & Management',
    description: 'Account setup, content operations, and management for two months.',
    price: 1_500,
    icon: ShareNetwork,
  },
]

const PACKAGE_OPTIONS: PackageOption[] = [
  {
    id: 'starter',
    name: 'Starter Pack',
    shortName: 'Starter',
    audience: 'For companies starting from zero in Japan',
    description: 'A complete market-entry foundation, from research through launch and early social operations.',
    price: 11_680,
    includedServiceIds: ['portfolio', 'research', 'landing-page', 'business-documents', 'social-media'],
    highlights: ['Market-entry strategy and research', 'Localized presence and launch materials', 'Two months of social operations'],
    badge: 'Recommended',
  },
  {
    id: 'localization',
    name: 'Localization Pack',
    shortName: 'Localization',
    audience: 'For products with an existing web presence',
    description: 'Localize the product itself, its marketing materials, and its social channels for Japan.',
    price: 7_480,
    includedServiceIds: ['product-localization', 'business-documents', 'social-media'],
    highlights: ['Product localization up to 5,000 strings', 'Marketing-material translation', 'Two months of social operations'],
  },
  {
    id: 'custom',
    name: 'Custom Build',
    shortName: 'Custom',
    audience: 'For a focused, modular engagement',
    description: 'Start with a blank configuration and add only the services your team needs now.',
    price: 0,
    includedServiceIds: [],
    highlights: ['No packaged starting cost', 'Choose individual deliverables', 'Easy to extend as priorities change'],
  },
]

const formatUsd = (amount: number) => `$${amount.toLocaleString('en-US')}`

export default function ConfiguratorPage() {
  const { t } = useLocale()
  usePageTitle(t("Configurator"))
  const { openInquiry } = useInquiry()
  const [packageId, setPackageId] = useState<PackageId>('starter')
  const [selectedServices, setSelectedServices] = useState<Set<ServiceId>>(new Set())
  const [retainer, setRetainer] = useState(false)
  const [performanceSales, setPerformanceSales] = useState(false)

  useEffect(() => {
    document.body.classList.add('body-white', 'body-configurator')
    return () => document.body.classList.remove('body-white', 'body-configurator')
  }, [])

  const activePackage = PACKAGE_OPTIONS.find(option => option.id === packageId) ?? PACKAGE_OPTIONS[0]
  const includedServices = useMemo(
    () => new Set<ServiceId>(activePackage.includedServiceIds),
    [activePackage],
  )

  const selectedAddOns = SERVICE_OPTIONS.filter(
    service => selectedServices.has(service.id) && !includedServices.has(service.id),
  )
  const oneTimeTotal = activePackage.price + selectedAddOns.reduce((sum, service) => sum + service.price, 0)
  const deliverableCount = includedServices.size + selectedAddOns.length

  const toggleService = (id: ServiceId) => {
    setSelectedServices(current => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const ongoingLabels = [
    retainer ? `${t('Retainer Plan')} (${formatUsd(2_480)} ${t('/ month')})` : null,
    performanceSales ? `${t('Performance Sales')} (${formatUsd(399)} ${t('/ qualified meeting')})` : null,
  ].filter(Boolean)

  const inquiryMessage = [
    t('I would like to discuss this Configurator estimate:'),
    t('Foundation: {plan} ({price} one-time)', { plan: t(activePackage.name), price: formatUsd(activePackage.price) }),
    t('Additional services: {services}', { services: selectedAddOns.length > 0 ? selectedAddOns.map(service => t(service.name)).join(', ') : t('None') }),
    t('Ongoing support: {services}', { services: ongoingLabels.length > 0 ? ongoingLabels.join(', ') : t('None') }),
    t('Estimated one-time total: {price}', { price: formatUsd(oneTimeTotal) }),
  ].join('\n')

  return (
    <>
      <Navbar lightMode />

      <main className="configurator-page">
        <div className="cfg-workspace cfg-container">
          <aside className="cfg-preview-column" aria-label={t("Configuration preview")}>
            <div className="cfg-preview-sticky">
              <div className="cfg-preview-stage" aria-hidden="true">
                <div className="cfg-stage-core">
                  <strong>{t(activePackage.name)}</strong>
                  <p>{t(activePackage.description)}</p>
                </div>
              </div>

              <div className="cfg-preview-summary">
                <div className="cfg-summary-row">
                  <span>{t("Foundation")}</span>
                  <strong>{t(activePackage.name)}</strong>
                </div>
                <div className="cfg-summary-row">
                  <span>{t("Deliverables")}</span>
                  <strong>{deliverableCount}</strong>
                </div>
                <div className="cfg-summary-row">
                  <span>{t("Ongoing services")}</span>
                  <strong>{Number(retainer) + Number(performanceSales)}</strong>
                </div>
              </div>
            </div>
          </aside>

          <div className="cfg-options-column">
            <fieldset className="cfg-section">
              <legend className="cfg-section-heading">
                <span>01</span>
                <span>
                  <strong>{t("Choose your foundation")}</strong>
                  <small>{t("Start with a complete pack or build from a blank slate.")}</small>
                </span>
              </legend>

              <div className="cfg-package-list">
                {PACKAGE_OPTIONS.map(option => {
                  const selected = option.id === packageId
                  return (
                    <label className={`cfg-package-card${selected ? ' is-selected' : ''}`} key={option.id}>
                      <input
                        type="radio"
                        name="foundation"
                        value={option.id}
                        checked={selected}
                        onChange={() => setPackageId(option.id)}
                      />
                      <span className="cfg-package-selector" aria-hidden="true">
                        {selected && <Check size={14} weight="bold" />}
                      </span>
                      <span className="cfg-package-content">
                        <span className="cfg-package-topline">
                          <span>
                            <strong>{t(option.name)}</strong>
                            {option.badge && <em>{t(option.badge)}</em>}
                          </span>
                          <span className="cfg-package-price">
                            {option.price === 0 ? t("No base cost") : formatUsd(option.price)}
                            {option.price > 0 && <small>{t("one-time")}</small>}
                          </span>
                        </span>
                        <span className="cfg-package-audience">{t(option.audience)}</span>
                        <span className="cfg-package-description">{t(option.description)}</span>
                        <span className="cfg-package-highlights">
                          {option.highlights.map(highlight => (
                            <span key={highlight}><Check size={13} weight="bold" />{t(highlight)}</span>
                          ))}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </fieldset>

            <fieldset className="cfg-section">
              <legend className="cfg-section-heading">
                <span>02</span>
                <span>
                  <strong>{t("Tailor the scope")}</strong>
                  <small>{t("Included modules are marked. Add any remaining services individually.")}</small>
                </span>
              </legend>

              <div className="cfg-service-list">
                {SERVICE_OPTIONS.map(service => {
                  const included = includedServices.has(service.id)
                  const selected = selectedServices.has(service.id) && !included
                  const IconComponent = service.icon
                  return (
                    <label
                      className={`cfg-service-row${included ? ' is-included' : ''}${selected ? ' is-selected' : ''}`}
                      key={service.id}
                    >
                      <input
                        type="checkbox"
                        checked={included || selected}
                        disabled={included}
                        onChange={() => toggleService(service.id)}
                      />
                      <span className="cfg-service-icon" aria-hidden="true">
                        <IconComponent size={20} weight="regular" />
                      </span>
                      <span className="cfg-service-copy">
                        <strong>{t(service.name)}</strong>
                        <small>{t(service.description)}</small>
                      </span>
                      <span className="cfg-service-end">
                        {included ? <em>{t("Included")}</em> : <strong>+{formatUsd(service.price)}</strong>}
                        <span className="cfg-check" aria-hidden="true">
                          {(included || selected) && <Check size={14} weight="bold" />}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </div>

              <p className="cfg-scope-note"> {t("Product localization above 5,000 strings, plus localization of future releases, is scoped separately.")} </p>
            </fieldset>

            <fieldset className="cfg-section cfg-section--last">
              <legend className="cfg-section-heading">
                <span>03</span>
                <span>
                  <strong>{t("Add ongoing support")}</strong>
                  <small>{t("Keep the local momentum going after the initial delivery.")}</small>
                </span>
              </legend>

              <div className="cfg-ongoing-grid">
                <label className={`cfg-ongoing-card${retainer ? ' is-selected' : ''}`}>
                  <input type="checkbox" checked={retainer} onChange={event => setRetainer(event.target.checked)} />
                  <span className="cfg-ongoing-icon"><ChartLineUp size={22} /></span>
                  <span className="cfg-ongoing-copy">
                    <span><strong>{t("Retainer Plan")}</strong><em>{formatUsd(2_480)} {t("/ month")}</em></span>
                    <small>{t("Social media management, analytics, and Japanese-language customer success.")}</small>
                  </span>
                  <span className="cfg-check" aria-hidden="true">{retainer && <Check size={14} weight="bold" />}</span>
                </label>

                <label className={`cfg-ongoing-card${performanceSales ? ' is-selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={performanceSales}
                    onChange={event => setPerformanceSales(event.target.checked)}
                  />
                  <span className="cfg-ongoing-icon"><Handshake size={22} /></span>
                  <span className="cfg-ongoing-copy">
                    <span><strong>{t("Performance Sales")}</strong><em>{formatUsd(399)} {t("/ qualified meeting")}</em></span>
                    <small>{t("Sales representation charged only when a meeting with a decision-maker is confirmed.")}</small>
                  </span>
                  <span className="cfg-check" aria-hidden="true">{performanceSales && <Check size={14} weight="bold" />}</span>
                </label>
              </div>
            </fieldset>
          </div>
        </div>

        <div className="cfg-price-spacer" aria-hidden="true" />

        <div className="cfg-price-bar" aria-live="polite" data-testid="configurator-total">
          <div className="cfg-total-primary">
            <span>{t("Estimated one-time total")}</span>
            <strong>{formatUsd(oneTimeTotal)}</strong>
          </div>
          <div className="cfg-total-recurring">
            {retainer && <span><strong>{formatUsd(2_480)}</strong> {t("/ month")}</span>}
            {performanceSales && <span><strong>{formatUsd(399)}</strong> {t("/ qualified meeting")}</span>}
            {!retainer && !performanceSales && <span>{t("No ongoing services selected")}</span>}
          </div>
          <SiteButton
            variant="accent"
            size="compact"
            onClick={() => openInquiry({ message: inquiryMessage })}
          > {t("Review with us")} <ArrowRight size={16} weight="bold" />
          </SiteButton>
        </div>
      </main>

      <Footer />
    </>
  )
}
